"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { LhubPlayerInstance } from "./lhubPlayer.types";

/**
 * Lets something OUTSIDE the player drive it — the OTT detail's chapter list
 * seeking the video beside it (N02). The player registers its video.js
 * instance here when it mounts and clears it on dispose; a sibling calls
 * `seekTo`. `currentTime` is the playhead in whole seconds, published by the
 * player on `timeupdate`, so a list can mark the chapter that is playing.
 *
 * Optional by design: a player rendered outside a provider (lessons, event
 * recordings) simply has nobody to register with. One player per provider —
 * the stage has one video.
 */
type PlayerSeek = {
  register: (player: LhubPlayerInstance | null) => void;
  /** Seek and play. False when no player has registered yet (the bundle is
   *  still loading, or playback is locked): the caller decides what to do. */
  seekTo: (seconds: number) => boolean;
  publishTime: (seconds: number) => void;
  currentTime: number | null;
};

const Ctx = createContext<PlayerSeek | null>(null);

export function PlayerSeekProvider({ children }: { children: ReactNode }) {
  const playerRef = useRef<LhubPlayerInstance | null>(null);
  const [currentTime, setCurrentTime] = useState<number | null>(null);

  const register = useCallback((player: LhubPlayerInstance | null) => {
    playerRef.current = player;
    if (!player) setCurrentTime(null);
  }, []);

  const publishTime = useCallback((seconds: number) => {
    // Whole seconds: a chapter row changes state once a second, not sixty
    // times, and React bails out of an unchanged value.
    if (Number.isFinite(seconds)) setCurrentTime(Math.floor(seconds));
  }, []);

  const seekTo = useCallback((seconds: number) => {
    const player = playerRef.current;
    if (!player?.currentTime) return false;
    player.currentTime(seconds);
    // play() answers a promise that rejects when the browser refuses autoplay
    // without a gesture; a chapter click IS a gesture, but a rejection must
    // not surface as an unhandled error either way.
    const started = player.play?.();
    if (started && typeof started.catch === "function") started.catch(() => {});
    setCurrentTime(Math.floor(seconds));
    return true;
  }, []);

  const value = useMemo<PlayerSeek>(
    () => ({ register, seekTo, publishTime, currentTime }),
    [register, seekTo, publishTime, currentTime],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The player's side: null outside a provider (nothing to register with).
 *  Referentially stable — both callbacks are — so the player's init effect
 *  can list it as a dependency without re-initialising on every render. */
export function usePlayerRegistry(): Pick<PlayerSeek, "register" | "publishTime"> | null {
  const ctx = useContext(Ctx);
  const register = ctx?.register;
  const publishTime = ctx?.publishTime;
  return useMemo(
    () => (register && publishTime ? { register, publishTime } : null),
    [register, publishTime],
  );
}

/** The list's side: seek, and the playhead to highlight against. */
export function usePlayerSeek(): Pick<PlayerSeek, "seekTo" | "currentTime"> {
  const ctx = useContext(Ctx);
  return ctx ?? { seekTo: () => false, currentTime: null };
}
